import { toast } from 'react-hot-toast';
import { Coffee, Square } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useBreaks, useBreakMutations } from '../hooks/useCallCenter';
import { BREAK_LABELS, BREAK_TYPES, formatDuration } from '../constants';
import type { BreakType } from '../types';

export function BreakControls() {
  const { data, isLoading, isError } = useBreaks();
  const { startBreak, endBreak } = useBreakMutations();

  const active = data?.active ?? null;
  const summary = data?.summary ?? [];
  const busy = startBreak.isPending || endBreak.isPending;

  const onStart = (type: BreakType) =>
    startBreak.mutate(type, {
      onSuccess: () => toast.success(`${BREAK_LABELS[type]} break started.`),
      onError: () => toast.error('Could not start break.'),
    });

  const onEnd = () =>
    endBreak.mutate(undefined, {
      onSuccess: (r) => toast.success(`Break ended (${formatDuration(r.duration_sec)}).`),
      onError: () => toast.error('Could not end break.'),
    });

  return (
    <Card>
      <CardHeader
        title={<span className="flex items-center gap-2"><Coffee className="h-4 w-4" /> Breaks</span>}
        action={active ? <Badge tone="warning">On {BREAK_LABELS[active.break_type] ?? active.break_type} break</Badge> : undefined}
      />
      <CardBody className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : isError ? (
          <p className="text-body-sm text-on-surface-variant">Failed to load break status.</p>
        ) : active ? (
          <div className="flex items-center justify-between rounded-card border border-warning/40 bg-warning/5 px-4 py-3">
            <span className="text-data-md text-on-surface">
              On <span className="font-medium">{BREAK_LABELS[active.break_type] ?? active.break_type}</span> break since{' '}
              {new Date(active.started_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <Button variant="danger" size="sm" loading={busy} onClick={onEnd}>
              <Square className="h-3.5 w-3.5" /> End Break
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {BREAK_TYPES.map((b) => (
              <Button key={b.value} variant="outline" size="sm" disabled={busy} onClick={() => onStart(b.value)}>
                {b.label}
              </Button>
            ))}
          </div>
        )}

        {summary.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {summary.map((s) => (
              <Badge key={s.break_type} tone="neutral">
                {BREAK_LABELS[s.break_type] ?? s.break_type}: {s.count} · {formatDuration(s.total_sec)}
              </Badge>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
